import targetTab from "../../../assets/svg/target_tab.svg";
import targetTabGreen from "../../../assets/svg/target_tab_green.svg";
import pathwayTab from "../../../assets/svg/transition_tab.svg";
import pathwayTabGreen from "../../../assets/svg/transition_tab_green.svg";
import climateActionTab from "../../../assets/svg/ca_tab.svg";
import climateActionTabGreen from "../../../assets/svg/ca_tab_green.svg";
import na100Tab from "../../../assets/svg/na100_tab.svg";
import na100TabGreen from "../../../assets/svg/na100_tab_green.svg";
import { Provider, TargetStatus } from "../../../models/CompanyprofileModels";
import { Column } from "../../../components/tables/FlexibleTable";
import { TagProps } from "../../../components/visuals/CompassTag";
import { statusBadgeColors } from "../../../styles";

export type ProviderTab = {
  title: string;
  icon: string;
  iconActive: string;

  iconSize?: number;
};
export const providerTabs: Record<Provider, ProviderTab> = {
  [Provider.SBTI]: {
    title: "Science Based Targets Initiative",
    icon: targetTab,
    iconActive: targetTabGreen,
  },
  [Provider.TPI]: {
    title: "Transition Pathway Initiative",
    icon: pathwayTab,
    iconActive: pathwayTabGreen,
  },
  [Provider.CA100]: {
    title: "Climate Action 100+ Company",
    icon: climateActionTab,
    iconActive: climateActionTabGreen,
    iconSize: 28,
  },
  [Provider.NZIF]: {
    title: "Net Zero Investment Framework",
    icon: climateActionTab,
    iconActive: climateActionTabGreen,
    iconSize: 28,
  },
  [Provider.NA100]: {
    title: "Nature Action 100",
    icon: na100Tab,
    iconActive: na100TabGreen,
    iconSize: 32,
  },
};

export enum SBTIMetric {
  Long_term_Target_Year = "Long_term_Target_Year",
  Long_term_Target_Status = "Long_term_Target_Status",
  Long_term_Target_Implied_Temperature = "Long_term_Target_Implied_Temperature",
  Near_term_Target_Year = "Near_term_Target_Year",
  Near_term_Target_Status = "Near_term_Target_Status",
  Near_term_Target_Implied_Temperature = "Near_term_Target_Implied_Temperature",
  Net_Zero_Committed = "Net_Zero_Committed",
  Net_Zero_Year = "Net_Zero_Year",
  Full_Target_Language = "Full_Target_Language",
}

export enum TPIMetric {
  // TPI Management Quality Score
  TPI_MQ_Score = "TPI_MQ_Score",
  // Level 0
  L0_Q1 = "TPI_MQ_Q1",

  // Level 1
  L1_Q1 = "TPI_MQ_Q2",
  L1_Q2 = "TPI_MQ_Q3",

  // Level 2
  L2_Q1 = "TPI_MQ_Q4",
  L2_Q2 = "TPI_MQ_Q5",

  // Level 3
  L3_Q1 = "TPI_MQ_Q6",
  L3_Q2 = "TPI_MQ_Q7",
  L3_Q3 = "TPI_MQ_Q8",
  L3_Q4 = "TPI_MQ_Q9",
  L3_Q5 = "TPI_MQ_Q10",
  L3_Q6 = "TPI_MQ_Q11",
  L3_Q7 = "TPI_MQ_Q12",

  // Level 4
  L4_Q1 = "TPI_MQ_Q13",
  L4_Q2 = "TPI_MQ_Q14",
  L4_Q3 = "TPI_MQ_Q15",
  L4_Q4 = "TPI_MQ_Q16",
  L4_Q5 = "TPI_MQ_Q17",
  L4_Q6 = "TPI_MQ_Q18",

  // Level 5
  L5_Q1 = "TPI_MQ_Q19",
  L5_Q2 = "TPI_MQ_Q20",
  L5_Q3 = "TPI_MQ_Q21",
  L5_Q4 = "TPI_MQ_Q22",
  L5_Q5 = "TPI_MQ_Q23",
}

export enum ClimateMaturityLevel {
  Unaware = "Unaware of Climate Change as a Business Issue",
  Acknowledging = "Acknowledging Climate Change as a Business Issue",
  BuildingCapacity = "Building Capacity",
  Integrating = "Integrating into Operational Decision Making",
  StrategicAssessment = "Strategic Assessment",
  TransitionPlanning = "Transition Planning and Implementation",
}

export type TPILevelMetric = {
  metric_id: TPIMetric;
  value: string | null | number;
  valueBoolean: boolean;
};

export type LegendAccordionPayload = {
  level: number;
  color: string;
  title: string;
  questions: {
    id: TPIMetric;
    text: string;
    status: boolean;
  }[];
};

export const SBTI_MESSAGE =
  "The targets covering greenhouse gas emissions from company operations (scopes 1 and 2) are consistent with reductions required to keep warming to 1.5°C";

export enum SBTIColumn {
  NEAR_TERM_TARGET_STATUS = "Near-term Target Status",
  LONG_TERM_TARGET_STATUS = "Long-term Target Status",
  TARGET_YEAR = "Target Year",
  TARGET_IMPLIED_TEMPERATURE = "Target Implied Temperature",
  NET_ZERO_STATUS = "Net Zero Status",
}

export const SBTIMetricColumnMapping: Record<SBTIMetric, SBTIColumn> = {
  [SBTIMetric.Long_term_Target_Year]: SBTIColumn.TARGET_YEAR,
  [SBTIMetric.Long_term_Target_Status]: SBTIColumn.LONG_TERM_TARGET_STATUS,
  [SBTIMetric.Long_term_Target_Implied_Temperature]:
    SBTIColumn.TARGET_IMPLIED_TEMPERATURE,
  [SBTIMetric.Near_term_Target_Year]: SBTIColumn.TARGET_YEAR,
  [SBTIMetric.Near_term_Target_Status]: SBTIColumn.NEAR_TERM_TARGET_STATUS,
  [SBTIMetric.Near_term_Target_Implied_Temperature]:
    SBTIColumn.TARGET_IMPLIED_TEMPERATURE,
  [SBTIMetric.Net_Zero_Committed]: SBTIColumn.NET_ZERO_STATUS,
  [SBTIMetric.Net_Zero_Year]: SBTIColumn.NET_ZERO_STATUS,
  // Not used
  [SBTIMetric.Full_Target_Language]: SBTIColumn.NET_ZERO_STATUS,
};

export const SBTI_NEAR_TERM_COLUMNS: Column[] = [
  { title: SBTIColumn.NEAR_TERM_TARGET_STATUS },
  { title: SBTIColumn.TARGET_YEAR },
  { title: SBTIColumn.TARGET_IMPLIED_TEMPERATURE },
];

export const SBTI_LONG_TERM_COLUMNS: Column[] = [
  { title: SBTIColumn.LONG_TERM_TARGET_STATUS },
  { title: SBTIColumn.TARGET_YEAR },
  { title: SBTIColumn.TARGET_IMPLIED_TEMPERATURE },
];

export const SBTI_NET_ZERO_COLUMNS: Column[] = [
  { title: SBTIColumn.NET_ZERO_STATUS },
  { title: SBTIColumn.TARGET_YEAR },
];

export const SBTI_TARGET_STATUS: TagProps[] = [
  {
    tagKey: TargetStatus.TARGETS_SET,
    displayValue: TargetStatus.TARGETS_SET,
    color: statusBadgeColors.Complete.text,
    bg: statusBadgeColors.Complete.bg,
  },
  {
    tagKey: TargetStatus.COMMITMENT_REMOVED,
    displayValue: TargetStatus.COMMITMENT_REMOVED,
    color: statusBadgeColors.Stuck.text,
    bg: statusBadgeColors.Stuck.bg,
  },
  {
    tagKey: TargetStatus.COMMITTED,
    displayValue: TargetStatus.COMMITTED,
    color: statusBadgeColors.Warning.text,
    bg: statusBadgeColors.Warning.bg,
  },
  {
    tagKey: TargetStatus.NOT_AVAILABLE,
    displayValue: TargetStatus.NOT_AVAILABLE,
    color: statusBadgeColors.Planned.text,
    bg: statusBadgeColors.Planned.bg,
  },
];

export type TPIScore = 0 | 1 | 2 | 3 | 4 | 5;

export const TPI_MANAGEMENT_SCORE_TO_BADGE_COLORS_MAPPING: Record<
  TPIScore,
  { bg: string; text: string }
> = {
  0: {
    bg: statusBadgeColors.Stuck.bg,
    text: statusBadgeColors.Stuck.text,
  },
  1: {
    bg: statusBadgeColors.Stuck.bg,
    text: statusBadgeColors.Stuck.text,
  },
  2: {
    bg: statusBadgeColors.Warning.bg,
    text: statusBadgeColors.Warning.text,
  },
  3: {
    bg: statusBadgeColors.Warning.bg,
    text: statusBadgeColors.Warning.text,
  },
  4: {
    bg: statusBadgeColors.Warning.bg,
    text: statusBadgeColors.Warning.text,
  },
  5: {
    bg: statusBadgeColors.Complete.bg,
    text: statusBadgeColors.Complete.text,
  },
};

export const TPI_LEVEL_METRICS: TPIMetric[][] = [
  // Level 0
  [TPIMetric.L0_Q1],
  // Level 1
  [TPIMetric.L1_Q1, TPIMetric.L1_Q2],
  // Level 2
  [TPIMetric.L2_Q1, TPIMetric.L2_Q2],
  // Level 3
  [
    TPIMetric.L3_Q1,
    TPIMetric.L3_Q2,
    TPIMetric.L3_Q3,
    TPIMetric.L3_Q4,
    TPIMetric.L3_Q5,
    TPIMetric.L3_Q6,
    TPIMetric.L3_Q7,
  ],
  // Level 4
  [
    TPIMetric.L4_Q1,
    TPIMetric.L4_Q2,
    TPIMetric.L4_Q3,
    TPIMetric.L4_Q4,
    TPIMetric.L4_Q5,
    TPIMetric.L4_Q6,
  ],
  // Level 5
  [
    TPIMetric.L5_Q1,
    TPIMetric.L5_Q2,
    TPIMetric.L5_Q3,
    TPIMetric.L5_Q4,
    TPIMetric.L5_Q5,
  ],
];

export const TPI_QUESTION_LABELS: Partial<Record<TPIMetric, string>> = {
  [TPIMetric.L0_Q1]:
    "Does the company acknowledge climate change as a significant issue for the business?",
  [TPIMetric.L1_Q1]:
    "Does the company recognise climate change as a relevant risk and/or opportunity for the business?",
  [TPIMetric.L1_Q2]:
    "Does the company have a policy or equivalent commitment to action on climate change?",
  [TPIMetric.L2_Q1]:
    "Has the company set greenhouse gas emission reduction targets?",
  [TPIMetric.L2_Q2]:
    "Has the company published information on its Scope 1 and 2 greenhouse gas emissions?",
  [TPIMetric.L3_Q1]:
    "Has the company nominated a board member or board committee with explicit responsibility for oversight of the climate change policy?",
  [TPIMetric.L3_Q2]:
    "Has the company set quantitative targets for reducing its greenhouse gas emissions?",
  [TPIMetric.L3_Q3]: "Does the company report on Scope 3 emissions?",
  [TPIMetric.L3_Q4]:
    "Has the company had its operational Scope 1 and/or 2 greenhouse gas emissions data verified?",
  [TPIMetric.L3_Q5]:
    "Does the company support domestic and international efforts to mitigate climate change?",
  [TPIMetric.L3_Q6]:
    "Does the company have a process to manage climate-related risks?",
  [TPIMetric.L3_Q7]:
    "Does the company disclose materially important Scope 3 emissions?",
  [TPIMetric.L4_Q1]:
    "Has the company set long-term quantitative targets for reducing its greenhouse gas emissions?",
  [TPIMetric.L4_Q2]:
    "Does the company's remuneration for senior executives incorporate climate change performance?",
  [TPIMetric.L4_Q3]:
    "Does the company incorporate climate change risks and opportunities in their strategy?",
  [TPIMetric.L4_Q4]: "Does the company undertake climate scenario planning?",
  [TPIMetric.L4_Q5]: "Does the company disclose an internal price of carbon?",
  [TPIMetric.L4_Q6]:
    "Does the company disclose the actions necessary to meet its emissions reduction targets?",
  [TPIMetric.L5_Q1]:
    "Does the company quantify the key elements of its emissions reduction strategy and the proportional impact of each action in achieving its targets?",
  [TPIMetric.L5_Q2]:
    "Does the company's transition plan clarify the role that will be played by offsets and/or negative emissions technologies?",
  [TPIMetric.L5_Q3]:
    "Does the company commit to phasing out capital expenditure on carbon-intensive assets or products?",
  [TPIMetric.L5_Q4]:
    "Does the company align future capital expenditures with its long-term decarbonisation goals and disclose how the alignment is determined?",
  [TPIMetric.L5_Q5]:
    "Does the company ensure consistency between its climate change policy and the positions taken by trade associations of which it is a member?",
};

export const DEFAULT_LABEL = "Missing label for metric";

export const RADIAL_COLORS: { filled: string; empty: string }[] = [
  { filled: "#9a9ce9", empty: "#e3e3f1" },
  { filled: "#1ee290", empty: "#d6f5e8" },
  { filled: "#fd7d96", empty: "#f8dbe1" },
  { filled: "#ffc60f", empty: "#f8f1cd" },
  { filled: "#a3dcef", empty: "#e0f2f9" },
  { filled: "#0a769d", empty: "#d0e5ed" },
];

export const TITLES = [
  "Unaware of Climate Change as a Business Issue",
  "Climate Change Recognized as a Risk",
  "Targets and Emissions Disclosure",
  "Governance, Risk, and Disclosure Practices",
  "Climate Strategy and Execution",
  "Decarbonization Plan and Alignment",
];

export const MANAGEMENT_QUALITY_TITLE =
  "Assessment of the company according to the management of its greenhouse gas emissions and of risks and opportunities related to the low-carbon transition.";

export const CLIMATE_MATURITY_LEVELS: ClimateMaturityLevel[] = [
  ClimateMaturityLevel.Unaware,
  ClimateMaturityLevel.Acknowledging,
  ClimateMaturityLevel.BuildingCapacity,
  ClimateMaturityLevel.Integrating,
  ClimateMaturityLevel.StrategicAssessment,
  ClimateMaturityLevel.TransitionPlanning,
];
